// Sidebar.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiHome, 
  FiPackage, 
  FiUser,
  FiLogOut,
  FiGrid,
  FiBox,
  FiLayers,
  FiShoppingBag,
  FiArchive,
  FiCube,
  FiChevronRight,
  FiGift,
  FiSettings,
  FiUsers,
  FiBarChart2,
  FiClock,
  FiDatabase,
  FiList,
  FiBookOpen,
  FiShield
} from 'react-icons/fi';
import { accountAPI } from '../utils/api';
import footerLogo from '../assets/images/footer.png';
import footerLogoBlack from '../assets/images/footer_black.png';

const Sidebar = ({ currentPage, setCurrentPage, onLogout }) => {
  const [accountName, setAccountName] = useState('Admin');
  const [accountRole, setAccountRole] = useState('');
  const [isHovered, setIsHovered] = useState(null);
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    fetchAccountInfo();
  }, []);

  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    checkDarkMode();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const fetchAccountInfo = async () => {
    try {
      const response = await accountAPI.getMe();
      if (response.data.success) {
        setAccountName(response.data.data.account_name || 'Admin');
        setAccountRole(response.data.data.role || '');
      }
    } catch (error) {
      console.error('Error fetching account info:', error);
    }
  };

  // Menu items configuration with role-based visibility
  const menuItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: FiHome,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400',
      roles: ['system', 'super_admin', 'reseller', 'user']
    },
    { 
      id: 'products', 
      label: 'Products', 
      icon: FiShoppingBag,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      iconColor: 'text-green-600 dark:text-green-400',
      roles: ['system', 'super_admin', 'reseller', 'user']
    },
    { 
      id: 'based-items', 
      label: 'Based Items', 
      icon: FiLayers,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
      iconColor: 'text-purple-600 dark:text-purple-400',
      roles: ['system', 'super_admin']
    },
    { 
      id: 'custom-items', 
      label: 'Packages', 
      icon: FiGift,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-100 dark:bg-pink-900/30',
      iconColor: 'text-pink-600 dark:text-pink-400',
      roles: ['system', 'super_admin', 'reseller', 'user']
    },
    { 
      id: 'stock-management', 
      label: 'Stock Management', 
      icon: FiDatabase,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-100 dark:bg-amber-900/30',
      iconColor: 'text-amber-600 dark:text-amber-400',
      roles: ['system', 'super_admin']
    },
    { 
      id: 'product-fields', 
      label: 'Product Fields', 
      icon: FiList,
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
      iconColor: 'text-indigo-600 dark:text-indigo-400',
      roles: ['system']
    },
    { 
      id: 'history', 
      label: 'History', 
      icon: FiClock,
      color: 'from-teal-500 to-cyan-500',
      bgColor: 'bg-teal-100 dark:bg-teal-900/30',
      iconColor: 'text-teal-600 dark:text-teal-400',
      roles: ['system', 'super_admin', 'reseller', 'user']
    },
    {
      id: 'guide',
      label: 'Guide',
      icon: FiBookOpen,
      color: 'from-sky-500 to-indigo-500',
      bgColor: 'bg-sky-100 dark:bg-sky-900/30',
      iconColor: 'text-sky-600 dark:text-sky-400',
      roles: ['system', 'super_admin', 'reseller', 'user']
    },
    // {
    //   id: 'admin-tools',
    //   label: 'Admin Tool',
    //   icon: FiShield,
    //   color: 'from-rose-500 to-orange-500',
    //   bgColor: 'bg-rose-100 dark:bg-rose-900/30',
    //   iconColor: 'text-rose-600 dark:text-rose-400',
    //   roles: ['system', 'super_admin', 'reseller']
    // },
    { 
      id: 'account', 
      label: 'Account', 
      icon: FiUser,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30',
      iconColor: 'text-orange-600 dark:text-orange-400',
      roles: ['system', 'super_admin', 'reseller', 'user']
    },
  ];

  // Get role display name
  const getRoleDisplay = (role) => {
    switch(role) {
      case 'system':
        return 'System Administrator';
      case 'super_admin':
        return 'Super Admin';
      case 'reseller':
        return 'Reseller';
      case 'user':
        return 'User';
      default:
        return 'Administrator';
    }
  };

  // Get role icon color
  const getRoleIconColor = (role) => {
    switch(role) {
      case 'system':
        return 'text-red-500';
      case 'super_admin':
        return 'text-purple-500';
      case 'reseller':
        return 'text-yellow-500';
      case 'user':
        return 'text-blue-500';
      default:
        return 'text-blue-500';
    }
  };

  const getLogoSrc = () => {
    return isDarkMode ? footerLogo : footerLogoBlack;
  };

  const getLogoStyle = () => {
    if (isDarkMode) {
      return 'opacity-90';
    }
    return 'brightness-90 contrast-110';
  };

  const visibleMenuItems = menuItems.filter(item => item.roles.includes(accountRole));

  return (
    <motion.aside
      initial={{ x: -280 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.3, type: "spring", stiffness: 100 }}
      className="fixed top-0 left-0 z-30 h-full w-64 bg-white dark:bg-gray-900 shadow-2xl flex flex-col"
    >
      <div className="flex flex-col h-full">
        {/* Logo Section */}
        <div className="relative flex items-center justify-center h-20 border-b border-gray-100 dark:border-gray-800">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5" />
          <div className="relative flex items-center">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-xl">P</span>
            </div>
            <div className="ml-3">
              <h1 className="text-xl font-bold">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">PTD</span>
                <span className="text-gray-800 dark:text-gray-200 ml-1">Admin</span>
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Management System</p>
            </div>
          </div>
        </div>
        
        {/* Menu Items */}
        <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto no-scrollbar">
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            
            return (
              <motion.button
                key={item.id}
                whileHover={{ x: 4 }}
                whileTap={{ scale: 0.98 }}
                onHoverStart={() => setIsHovered(item.id)}
                onHoverEnd={() => setIsHovered(null)}
                onClick={() => {
                  setCurrentPage(item.id, {});
                }}
                className={`w-full relative group`}
              >
                <div className={`
                  flex items-center px-4 py-3 rounded-xl transition-all duration-200
                  ${isActive 
                    ? `bg-gradient-to-r ${item.color} text-white shadow-lg` 
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }
                `}>
                  {/* Icon */}
                  <div className={`
                    p-2 rounded-lg transition-all duration-200
                    ${isActive 
                      ? 'bg-white/20' 
                      : `${item.bgColor} group-hover:scale-110`
                    }
                  `}>
                    <Icon className={`
                      w-4 h-4 transition-all duration-200
                      ${isActive ? 'text-white' : item.iconColor}
                    `} />
                  </div>
                  
                  {/* Label */}
                  <span className="text-sm font-medium ml-3">{item.label}</span>
                  
                  {/* Active Indicator */}
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute right-3 w-1.5 h-8 bg-white rounded-full"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </div>
              </motion.button>
            );
          })}
        </nav>
        
        {/* User Section */}
        <div className="relative border-t border-gray-100 dark:border-gray-800">
          {/* User Profile Card */}
          <div className="px-4 pt-4 pb-2">
            <motion.div
              whileHover={{ y: -2 }}
              className="relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-4"
            >
              {/* Decorative gradient */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full transform translate-x-10 -translate-y-10" />
              
              <div className="relative flex items-center space-x-3">
                {/* Avatar with status */}
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-lg shadow-lg">
                    {accountName.charAt(0).toUpperCase()}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full"></div>
                </div>
                
                {/* User Details */}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {accountName}
                  </p>
                  <div className="flex items-center mt-1 space-x-1">
                    <FiUser className={`w-3 h-3 ${getRoleIconColor(accountRole)}`} />
                    <span className="text-xs text-gray-600 dark:text-gray-300">
                      {getRoleDisplay(accountRole)}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sign Out Button */}
          <div className="px-4 pb-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onLogout}
              className="w-full flex items-center justify-center px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-colors shadow-md relative overflow-hidden group"
            >
              {/* Shine animation on hover */}
              <motion.div
                className="absolute inset-0 bg-white/20"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
              />
              <FiLogOut className="w-4 h-4 mr-2 relative z-10" />
              <span className="text-sm font-semibold relative z-10">Sign Out</span>
            </motion.button>
          </div>

          {/* Developed by Section - Smooth Sliding Gradient Effect */}
          <div className="px-4 pb-4">
            <motion.div 
              className="relative overflow-hidden rounded-lg"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Background gradient that slides */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-100/20 to-transparent dark:via-blue-900/20"
                animate={{
                  x: ['-100%', '200%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              
              {/* Content */}
              <div className="relative flex items-center justify-center gap-2 py-2">
                <motion.span 
                  className="text-xs text-gray-500 dark:text-gray-400"
                  animate={{
                    color: ['#6b7280', '#8b5cf6', '#6b7280'],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  Developed by
                </motion.span>
                
                <motion.div
                  onHoverStart={() => setIsLogoHovered(true)}
                  onHoverEnd={() => setIsLogoHovered(false)}
                  animate={{
                    scale: isLogoHovered ? 1.1 : 1,
                    rotate: isLogoHovered ? [0, -5, 5, -5, 0] : 0,
                  }}
                  transition={{
                    scale: { type: "spring", stiffness: 400, damping: 15 },
                    rotate: { duration: 0.5, ease: "easeInOut" },
                  }}
                >
                  <img 
                    src={getLogoSrc()}
                    alt="ATA Code House" 
                    className={`h-6 w-auto inline-block transition-all duration-300 ${getLogoStyle()}`}
                    style={{
                      filter: isDarkMode ? 'brightness(1.2) contrast(1.2)' : 'brightness(1.1) contrast(1.2)'
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      const parent = e.target.parentElement;
                      const fallback = document.createElement('span');
                      fallback.className = 'text-xs font-semibold text-indigo-600 dark:text-indigo-400';
                      fallback.textContent = 'ATA Code House';
                      parent.appendChild(fallback);
                    }}
                  />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </motion.aside>
  );
};

export default Sidebar;
